import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';
import { StorageLink } from '../global/storageUrls';

// All non-controlled medications for reference
const ALL_MEDICATIONS = [
  'Acetaminophen IV', 'Adenosine', 'Albuterol', 'Amiodarone', 'Aspirin',
  'Atropine', 'Calcium Chloride', 'Cardene', 'Dexamethasone', 'Dextrose 10% IV',
  'Dextrose 25%', 'Dextrose 50%', 'Diltiazem', 'Diphenhydramine', 'Dobutamine',
  'Droperidol', 'Duoneb', 'Epi (1:1000)', 'Epi (1:10000)', 'Furosemide',
  'Glucagon', 'Glutose', 'Ketorolac', 'Labetalol', 'Levetiracetam',
  'Lidocaine', 'Mag Sulfate', 'Mannitol', 'Narcan', 'Nitro Infusion',
  'Nitro Tabs', 'Norepinephrine', 'Ondansetron IV', 'Ondansetron ODT',
  'Oxytocin/Pitocin', 'Phenylephrine', 'Potassium', 'Racemic Epi',
  'Sodium Bicarb', 'Sterile Water', 'TXA', 'Vasopressin'
];

const ACTION_TYPES = [
  'Patient Use Restock',
  'Truck Restock',
  'Inventory/Audit',
  'Removing Expired Medication',
  'Cabinet Inventory Restock'
];

const getActionColor = (action) => {
  switch (action) {
    case 'Patient Use Restock': return { bg: '#e3f2fd', color: '#1565c0' };
    case 'Truck Restock': return { bg: '#e8f5e9', color: '#2e7d32' };
    case 'Inventory/Audit': return { bg: '#fff3e0', color: '#e65100' };
    case 'Removing Expired Medication': return { bg: '#fce4ec', color: '#c62828' };
    case 'Cabinet Inventory Restock': return { bg: '#f3e5f5', color: '#6a1b9a' };
    default: return { bg: '#f5f5f5', color: '#333' };
  }
};

function NonControlledReport({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [allRecords, setAllRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [inventorySummary, setInventorySummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [uniqueUsers, setUniqueUsers] = useState([]);

  useEffect(() => {
    if (isAdmin) {
      fetchAllRecords();
    }
  }, [isAdmin]);

  // Apply filters whenever records or filter values change
  useEffect(() => {
    applyFilters();
  }, [allRecords, filterAction, filterUser, filterDateFrom, filterDateTo]);

  const fetchAllRecords = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('non_controlled_medications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const records = data || [];
      setAllRecords(records);

      // Extract unique users
      const users = [...new Set(records.map(r => `${r.first_name} ${r.last_name}`))].sort();
      setUniqueUsers(users);

      // Build inventory summary
      buildInventorySummary(records);
    } catch (err) {
      console.error('Error fetching records:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildInventorySummary = (records) => {
    const summary = {};

    // Initialize all medications at 0
    ALL_MEDICATIONS.forEach(med => {
      summary[med] = { restocked: 0, removed: 0, net: 0 };
    });

    records.forEach(record => {
      if (!record.medication_items_json) return;

      let items;
      try {
        items = JSON.parse(record.medication_items_json);
      } catch {
        return;
      }

      items.forEach(item => {
        const medName = item.medication;
        const amount = parseInt(item.amount) || 0;

        if (!summary[medName]) {
          summary[medName] = { restocked: 0, removed: 0, net: 0 };
        }

        if (record.action === 'Cabinet Inventory Restock') {
          summary[medName].restocked += amount;
          summary[medName].net += amount;
        } else if (['Patient Use Restock', 'Truck Restock', 'Removing Expired Medication'].includes(record.action)) {
          summary[medName].removed += amount;
          summary[medName].net -= amount;
        }
      });
    });

    setInventorySummary(summary);
  };

  const applyFilters = () => {
    let filtered = [...allRecords];

    if (filterAction) {
      filtered = filtered.filter(r => r.action === filterAction);
    }

    if (filterUser) {
      filtered = filtered.filter(r => `${r.first_name} ${r.last_name}` === filterUser);
    }

    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      from.setHours(0, 0, 0, 0);
      filtered = filtered.filter(r => new Date(r.created_at) >= from);
    }

    if (filterDateTo) {
      const to = new Date(filterDateTo);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter(r => new Date(r.created_at) <= to);
    }

    setFilteredRecords(filtered);
  };

  const clearFilters = () => {
    setFilterAction('');
    setFilterUser('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const parseMedItems = (jsonStr) => {
    if (!jsonStr) return [];
    try {
      return JSON.parse(jsonStr);
    } catch {
      return [];
    }
  };

  const downloadCSV = () => {
    if (filteredRecords.length === 0) {
      alert('No data to download');
      return;
    }

    let csv = 'Date,Time,First Name,Last Name,Email,Action,Run Number,Truck Number,Medications,Signature,Notes\n';

    filteredRecords.forEach(record => {
      const date = new Date(record.created_at);
      const formattedDate = date.toLocaleDateString('en-US');
      const formattedTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

      const medItems = parseMedItems(record.medication_items_json);
      const medsStr = medItems.map(item => {
        let s = `${item.medication}${item.concentration ? ' (' + item.concentration + ')' : ''} x${item.amount}`;
        if (item.expiration_date) s += ` [exp: ${item.expiration_date}]`;
        return s;
      }).join('; ');

      let row = [
        formattedDate, formattedTime,
        record.first_name || '', record.last_name || '', record.email || '',
        record.action || '', record.run_number || '', record.truck_number || '',
        medsStr || '', record.primary_signature_url || '', record.additional_notes || ''
      ];

      row = row.map(field => `"${String(field).replace(/"/g, '""')}"`);
      csv += row.join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', 'Non_Controlled_Medications_Report.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Access control
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have administrator privileges.</p>
          <button onClick={() => onNavigate('home')} className="btn btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Medications that have any activity
  const activeMeds = Object.entries(inventorySummary)
    .filter(([_, data]) => data.restocked > 0 || data.removed > 0)
    .sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <HamburgerMenu isAdmin={isAdmin} onLogout={onLogout} onNavigate={onNavigate} />

      <div style={{ padding: '80px 20px 20px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Back Button */}
        <button
          onClick={() => onNavigate('admin-dashboard')}
          style={{
            padding: '8px 16px',
            border: '2px solid #0E3293',
            borderRadius: '6px',
            background: 'white',
            color: '#0E3293',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          ← Back to Admin
        </button>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
          padding: '30px',
          borderRadius: '12px',
          marginBottom: '30px',
          color: '#fff',
          boxShadow: '0 4px 12px rgba(46, 125, 50, 0.15)'
        }}>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px' }}>Non-Controlled Medications</h1>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
            Inventory activity summary and submission logs for EMS Station non-controlled medications
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#2e7d32' }}>
            <div style={{ fontSize: '18px', marginBottom: '10px' }}>Loading Report...</div>
          </div>
        ) : (
          <>
            {/* ===== INVENTORY SUMMARY ===== */}
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '25px',
              marginBottom: '30px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h2 style={{
                color: '#333',
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '5px'
              }}>
                Medication Activity Summary
              </h2>
              <p style={{ color: '#666', fontSize: '13px', marginBottom: '20px' }}>
                Aggregated totals across all submissions. "Restocked" includes Cabinet Inventory Restock. "Removed" includes Patient Use Restock, Truck Restock, and Removing Expired Medication.
              </p>

              {activeMeds.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                  No medication activity recorded yet.
                </p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr style={{ background: '#2e7d32', color: 'white' }}>
                        <th style={{ padding: '12px 15px', textAlign: 'left', fontWeight: '600' }}>Medication</th>
                        <th style={{ padding: '12px 15px', textAlign: 'center', fontWeight: '600' }}>Restocked</th>
                        <th style={{ padding: '12px 15px', textAlign: 'center', fontWeight: '600' }}>Removed</th>
                        <th style={{ padding: '12px 15px', textAlign: 'center', fontWeight: '600' }}>Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeMeds.map(([medName, data], index) => (
                        <tr key={medName} style={{
                          borderBottom: '1px solid #e0e0e0',
                          background: index % 2 === 0 ? '#f8f9fa' : 'white'
                        }}>
                          <td style={{ padding: '10px 15px', fontWeight: '500' }}>{medName}</td>
                          <td style={{ padding: '10px 15px', textAlign: 'center', color: '#2e7d32', fontWeight: '600' }}>
                            +{data.restocked}
                          </td>
                          <td style={{ padding: '10px 15px', textAlign: 'center', color: '#c62828', fontWeight: '600' }}>
                            {data.removed > 0 ? `-${data.removed}` : '0'}
                          </td>
                          <td style={{
                            padding: '10px 15px',
                            textAlign: 'center',
                            fontWeight: '700',
                            color: data.net >= 0 ? '#2e7d32' : '#c62828'
                          }}>
                            {data.net >= 0 ? `+${data.net}` : data.net}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ===== FILTERS ===== */}
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '25px',
              marginBottom: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h2 style={{
                color: '#333',
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '20px'
              }}>
                Activity Log
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px',
                marginBottom: '15px'
              }}>
                {/* Action Filter */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '5px' }}>
                    Action Type
                  </label>
                  <select
                    className="form-input"
                    value={filterAction}
                    onChange={(e) => setFilterAction(e.target.value)}
                    style={{ margin: 0 }}
                  >
                    <option value="">All Actions</option>
                    {ACTION_TYPES.map(act => (
                      <option key={act} value={act}>{act}</option>
                    ))}
                  </select>
                </div>

                {/* User Filter */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '5px' }}>
                    User
                  </label>
                  <select
                    className="form-input"
                    value={filterUser}
                    onChange={(e) => setFilterUser(e.target.value)}
                    style={{ margin: 0 }}
                  >
                    <option value="">All Users</option>
                    {uniqueUsers.map(user => (
                      <option key={user} value={user}>{user}</option>
                    ))}
                  </select>
                </div>

                {/* Date From */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '5px' }}>
                    From Date
                  </label>
                  <input
                    type="date"
                    className="form-input"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    style={{ margin: 0 }}
                  />
                </div>

                {/* Date To */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '5px' }}>
                    To Date
                  </label>
                  <input
                    type="date"
                    className="form-input"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    style={{ margin: 0 }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  onClick={clearFilters}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    background: 'white',
                    color: '#666',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Clear Filters
                </button>
                <button
                  className="btn btn-primary"
                  onClick={downloadCSV}
                  disabled={filteredRecords.length === 0}
                  style={{ fontSize: '13px', padding: '8px 16px' }}
                >
                  Download CSV
                </button>
                <span style={{ color: '#666', fontSize: '13px' }}>
                  {filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''}
                  {(filterAction || filterUser || filterDateFrom || filterDateTo) && ` (filtered from ${allRecords.length})`}
                </span>
              </div>
            </div>

            {/* ===== ACTIVITY TABLE ===== */}
            <div style={{
              background: '#fff',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {filteredRecords.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
                  No records found matching your filters.
                </p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr style={{ background: '#0E3293', color: 'white' }}>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Action</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Details</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Medications</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Signature</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecords.map((record, index) => {
                        const date = new Date(record.created_at);
                        const formattedDate = date.toLocaleDateString('en-US');
                        const formattedTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                        const medItems = parseMedItems(record.medication_items_json);
                        const actionColors = getActionColor(record.action);
                        const isExpanded = expandedRow === index;

                        let details = '';
                        if (record.run_number) details = `Run #: ${record.run_number}`;
                        if (record.truck_number) details = `Truck: ${record.truck_number}`;

                        return (
                          <tr key={record.id || index} style={{
                            background: index % 2 === 0 ? '#f8f9fa' : 'white',
                            borderBottom: '1px solid #e0e0e0'
                          }}>
                            <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{formattedDate}</td>
                            <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{formattedTime}</td>
                            <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                              {record.first_name} {record.last_name}
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                backgroundColor: actionColors.bg,
                                color: actionColors.color,
                                whiteSpace: 'nowrap'
                              }}>
                                {record.action}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              {details || <span style={{ color: '#999' }}>-</span>}
                            </td>
                            <td style={{ padding: '10px 12px', minWidth: '200px' }}>
                              {medItems.length === 0 ? (
                                <span style={{ color: '#999' }}>-</span>
                              ) : (
                                <div>
                                  {medItems.slice(0, isExpanded ? medItems.length : 2).map((item, i) => (
                                    <div key={i} style={{
                                      padding: '2px 0',
                                      fontSize: '13px',
                                      borderBottom: i < (isExpanded ? medItems.length : Math.min(medItems.length, 2)) - 1 ? '1px solid #eee' : 'none'
                                    }}>
                                      <strong>{item.medication}</strong>
                                      {item.concentration && (
                                        <span style={{ color: '#666' }}> ({item.concentration})</span>
                                      )}
                                      <span style={{ color: '#0E3293', fontWeight: '600' }}> x{item.amount}</span>
                                      {item.expiration_date && (
                                        <span style={{ color: '#c62828', fontSize: '12px' }}> [exp: {item.expiration_date}]</span>
                                      )}
                                    </div>
                                  ))}
                                  {medItems.length > 2 && (
                                    <button
                                      onClick={() => setExpandedRow(isExpanded ? null : index)}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#0E3293',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        padding: '3px 0'
                                      }}
                                    >
                                      {isExpanded ? 'Show less' : `+${medItems.length - 2} more...`}
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              {record.primary_signature_url ? (
                                <StorageLink value={record.primary_signature_url} style={{ color: '#0E3293' }}>
                                  View
                                </StorageLink>
                              ) : '-'}
                            </td>
                            <td style={{ padding: '10px 12px', maxWidth: '200px' }}>
                              {record.additional_notes || <span style={{ color: '#999' }}>-</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Refresh Button */}
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
              <button
                onClick={fetchAllRecords}
                className="btn btn-primary"
                style={{
                  padding: '12px 30px',
                  fontSize: '16px',
                  background: '#2e7d32',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                onMouseOver={(e) => e.target.style.background = '#43a047'}
                onMouseOut={(e) => e.target.style.background = '#2e7d32'}
              >
                Refresh Data
              </button>
            </div>
          </>
        )}
      </div>

      <footer className="footer">
        <p className="text-small">
          Non-Controlled Medications Report - NarcoticTrack © 2025 NarcoticTrack Inventory System, LLC
        </p>
      </footer>
    </div>
  );
}

export default NonControlledReport;
